import { useEffect, useState } from 'react'
import { createFileRoute, useSearch } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { SectionCard } from '@/components/shared/section-card'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Trash2, Send, Plus, X, Hash, Mail, MessageSquare } from 'lucide-react'
import { toast } from 'sonner'
import {
  useDiscordBotStatus,
  useDiscordChannels,
  useNotificationRules,
  useConnectDiscord,
  useDisconnectDiscord,
  useTestDiscordChannel,
  useSaveNotificationRules,
} from '@/hooks/use-notifications'
import type {
  DiscordChannel,
  NotificationEvent,
  SaveRuleInput,
} from '@/lib/api/modules/notifications'

export const Route = createFileRoute('/_app/settings/notifications')({
  component: NotificationsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    discord: (search.discord as string) || undefined,
  }),
})

// ---- Discord logo (matching integrations page pattern) ----

function DiscordLogo({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded-lg bg-[#5865F2] text-white font-bold ${className}`}
    >
      D
    </div>
  )
}

// ---- Main page ----

function NotificationsPage() {
  const search = useSearch({ from: '/_app/settings/notifications' })
  const { data: discordStatus, isLoading: isLoadingStatus } = useDiscordBotStatus()
  const { data: channels } = useDiscordChannels(discordStatus?.connected ?? false)
  const { data: rules, isLoading: isLoadingRules } = useNotificationRules()

  const connectDiscord = useConnectDiscord()
  const disconnectDiscord = useDisconnectDiscord()
  const testChannel = useTestDiscordChannel()
  const saveRules = useSaveNotificationRules()

  // Show success toast on OAuth redirect
  useEffect(() => {
    if (search.discord === 'connected') {
      toast.success('Discord conectado com sucesso')
      // Clean up URL param without triggering a navigation
      window.history.replaceState({}, '', '/settings/notifications')
    }
  }, [search.discord])

  if (isLoadingStatus) {
    return <PageSkeleton sections={2} />
  }

  const isConnected = discordStatus?.connected ?? false

  return (
    <div className="space-y-6">
      <PageHeader
        size="sm"
        title="Notificacoes"
        description="Configure onde e como receber notificacoes dos eventos do Rafobase."
      />

      {/* Providers section */}
      <SectionCard
        title="Providers"
        description="Conecte um provider para comecar a receber notificacoes."
      >
        <div className="space-y-3">
          {/* Discord */}
          {isConnected ? (
            <ConnectedProvider
              guildName={discordStatus?.guildName}
              onTest={() => {
                // Test with the first channel that has a rule, or the first available channel
                const firstChannel = channels?.[0]
                if (firstChannel) {
                  testChannel.mutate(firstChannel.id)
                } else {
                  toast.error('Nenhum canal disponivel para teste')
                }
              }}
              isTestPending={testChannel.isPending}
              onDisconnect={() => disconnectDiscord.mutate()}
              isDisconnectPending={disconnectDiscord.isPending}
            />
          ) : (
            <ProviderCard
              name="Discord"
              description="Receba notificacoes nos canais do seu servidor."
              logo={<DiscordLogo className="h-9 w-9 text-sm" />}
              action={
                <ChonkyButton
                  size="sm"
                  onClick={() => connectDiscord.mutate()}
                  loading={connectDiscord.isPending}
                >
                  Conectar Discord
                </ChonkyButton>
              }
            />
          )}

          {/* Future providers */}
          <ProviderCard
            name="Email"
            description="Receba alertas por e-mail."
            logo={
              <div className="flex items-center justify-center rounded-lg bg-muted h-9 w-9">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
            }
            action={
              <Badge variant="secondary" className="text-[11px]">
                Em breve
              </Badge>
            }
            disabled
          />

          <ProviderCard
            name="Slack"
            description="Envie notificacoes para canais do Slack."
            logo={
              <div className="flex items-center justify-center rounded-lg bg-muted h-9 w-9">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </div>
            }
            action={
              <Badge variant="secondary" className="text-[11px]">
                Em breve
              </Badge>
            }
            disabled
          />
        </div>
      </SectionCard>

      {/* Routing section - only when connected */}
      {isConnected && (
        <SectionCard
          title="Routing"
          description="Defina para quais canais cada evento envia notificacoes."
        >
          {isLoadingRules ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted/40 rounded-md animate-pulse" />
              ))}
            </div>
          ) : rules && rules.length > 0 ? (
            <RoutingTable
              events={rules}
              channels={channels ?? []}
              onAddDestination={(eventType, channelId, channelName) => {
                const event = rules.find((e) => e.eventType === eventType)
                if (!event) return

                const allRules: SaveRuleInput[] = buildRulesFromEvents(rules)

                // Add the new destination
                allRules.push({
                  eventType,
                  provider: 'discord',
                  channelId,
                  channelName,
                  isActive: true,
                })

                saveRules.mutate(allRules)
              }}
              onRemoveDestination={(eventType, channelId) => {
                const allRules = buildRulesFromEvents(rules).filter(
                  (r) => !(r.eventType === eventType && r.channelId === channelId)
                )
                saveRules.mutate(allRules)
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Nenhum evento configurado. Os eventos aparecerao aqui apos o backend retornar a lista.
            </p>
          )}
        </SectionCard>
      )}

      {/* Empty state when disconnected */}
      {!isConnected && (
        <div className="rounded-lg border border-dashed border-border/60 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhum provider conectado. Conecte pelo menos um para configurar o routing de
            notificacoes.
          </p>
        </div>
      )}
    </div>
  )
}

// ---- Sub-components ----

function ProviderCard({
  name,
  description,
  logo,
  action,
  disabled,
}: {
  name: string
  description: string
  logo: React.ReactNode
  action: React.ReactNode
  disabled?: boolean
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border border-border/60 px-4 py-3 ${
        disabled ? 'opacity-50' : ''
      }`}
    >
      {logo}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium">{name}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  )
}

function ConnectedProvider({
  guildName,
  onTest,
  isTestPending,
  onDisconnect,
  isDisconnectPending,
}: {
  guildName?: string
  onTest: () => void
  isTestPending: boolean
  onDisconnect: () => void
  isDisconnectPending: boolean
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-lg border border-green-200 dark:border-green-900/50 bg-green-50/50 dark:bg-green-950/20 px-4 py-3">
        <DiscordLogo className="h-9 w-9 text-sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-medium">Discord</p>
            <Badge variant="default" className="text-[11px] bg-green-600 hover:bg-green-600">
              Conectado
            </Badge>
          </div>
          {guildName && <p className="text-xs text-muted-foreground">{guildName}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onTest} disabled={isTestPending}>
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {isTestPending ? 'Enviando...' : 'Testar'}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={isDisconnectPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Desconectar Discord?</AlertDialogTitle>
                <AlertDialogDescription>
                  Todas as regras de notificacao para o Discord serao removidas. Voce pode
                  reconectar a qualquer momento.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDisconnect}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Desconectar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  )
}

// ---- Routing Table ----

function RoutingTable({
  events,
  channels,
  onAddDestination,
  onRemoveDestination,
}: {
  events: NotificationEvent[]
  channels: DiscordChannel[]
  onAddDestination: (eventType: string, channelId: string, channelName: string) => void
  onRemoveDestination: (eventType: string, channelId: string) => void
}) {
  // Group events by category
  const categories = events.reduce<Record<string, NotificationEvent[]>>((acc, event) => {
    if (!acc[event.category]) {
      acc[event.category] = []
    }
    acc[event.category].push(event)
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {Object.entries(categories).map(([category, categoryEvents]) => (
        <div key={category}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {category}
          </p>
          <div className="space-y-1">
            {categoryEvents.map((event) => (
              <EventRow
                key={event.eventType}
                event={event}
                channels={channels}
                onAddDestination={onAddDestination}
                onRemoveDestination={onRemoveDestination}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function EventRow({
  event,
  channels,
  onAddDestination,
  onRemoveDestination,
}: {
  event: NotificationEvent
  channels: DiscordChannel[]
  onAddDestination: (eventType: string, channelId: string, channelName: string) => void
  onRemoveDestination: (eventType: string, channelId: string) => void
}) {
  // Filter out channels already assigned to this event
  const assignedChannelIds = new Set(event.destinations.map((d) => d.channelId))
  const availableChannels = channels.filter((c) => !assignedChannelIds.has(c.id))

  // Group available channels by category
  const channelsByCategory = availableChannels.reduce<Record<string, DiscordChannel[]>>(
    (acc, channel) => {
      const cat = channel.categoryName || 'Sem categoria'
      if (!acc[cat]) {
        acc[cat] = []
      }
      acc[cat].push(channel)
      return acc
    },
    {}
  )

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 rounded-lg border border-border/40 px-4 py-3">
      {/* Event info */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium">{event.label}</p>
        <p className="text-xs text-muted-foreground">{event.description}</p>
      </div>

      {/* Destinations + add button */}
      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        {event.destinations.map((dest) => (
          <DestinationChip
            key={`${dest.provider}-${dest.channelId}`}
            provider={dest.provider}
            channelName={dest.channelName}
            onRemove={() => onRemoveDestination(event.eventType, dest.channelId)}
          />
        ))}

        <ChannelPicker
          channelsByCategory={channelsByCategory}
          onSelect={(channel) => onAddDestination(event.eventType, channel.id, channel.name)}
        />
      </div>
    </div>
  )
}

function DestinationChip({
  provider,
  channelName,
  onRemove,
}: {
  provider: string
  channelName: string
  onRemove: () => void
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-muted/80 border border-border/40 px-2 py-1 text-xs font-medium transition-transform hover:-translate-y-px">
      {provider === 'discord' && <Hash className="h-3 w-3 text-[#5865F2]" />}
      {channelName}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 rounded-sm p-0.5 hover:bg-muted-foreground/20 transition-colors"
        aria-label={`Remover ${channelName}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

function ChannelPicker({
  channelsByCategory,
  onSelect,
}: {
  channelsByCategory: Record<string, DiscordChannel[]>
  onSelect: (channel: DiscordChannel) => void
}) {
  const [open, setOpen] = useState(false)
  const hasChannels = Object.keys(channelsByCategory).length > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          disabled={!hasChannels}
        >
          <Plus className="h-3 w-3 mr-1" />
          Adicionar
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <Command>
          <CommandInput placeholder="Buscar canal..." />
          <CommandList>
            <CommandEmpty>Nenhum canal encontrado.</CommandEmpty>
            {Object.entries(channelsByCategory).map(([category, categoryChannels]) => (
              <CommandGroup key={category} heading={category}>
                {categoryChannels.map((channel) => (
                  <CommandItem
                    key={channel.id}
                    value={channel.name}
                    onSelect={() => {
                      onSelect(channel)
                      setOpen(false)
                    }}
                  >
                    <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                    {channel.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ---- Helpers ----

function buildRulesFromEvents(events: NotificationEvent[]): SaveRuleInput[] {
  return events.flatMap((event) =>
    event.destinations.map((dest) => ({
      eventType: event.eventType,
      provider: dest.provider,
      channelId: dest.channelId,
      channelName: dest.channelName,
      isActive: dest.isActive,
    }))
  )
}
